import { zodResolver } from "@hookform/resolvers/zod";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { COUNTRY_OPTIONS, FormRow } from "@/components/form";
import { todayDateOnly } from "@/lib/dateOnly";

import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import { ForeignNationalDocumentsAPI } from "../../../api/foreignNationalDocumentsApi";
import type {
  DuplicateMatch,
  PassportScan
} from "../../../api/types/clientDirectory";
import { useGetUserDetails } from "../../../hooks/useGetUserDetails";
import { usePracticeTeamMembers } from "../../../hooks/usePracticeTeamMembers";
import { useUserRoleAndSubscription } from "../../../hooks/useUserRoleAndSubscription";
import { AssigneePicker } from "../../../pages/ForeignNationals/create/AssigneePicker";
import {
  applyScan,
  duplicateQuery,
  emptyNewClient,
  newClientSchema,
  OPTIONAL_FIELDS,
  passportFromScan,
  profileUpdates,
  rankDuplicates,
  toCreatePayload,
  type NewClientValues,
  type OptionalField
} from "../../../pages/ForeignNationals/create/createModel";
import { DuplicateWarning } from "../../../pages/ForeignNationals/create/DuplicateWarning";
import { PassportScanDrop } from "../../../pages/ForeignNationals/create/PassportScanDrop";
import { LANGUAGE_GROUPS } from "../../clientProfile/tabs/personal/personalModel";
import { OptionalFieldTags } from "../../foreignNationals/components/ClientDetailsFields";
import { useForeignNationals } from "../../foreignNationals/context/ForeignNationalsContext";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";
import {
  DialogQuestion,
  NewCaseDialogFrame
} from "../../newCase/components/NewCaseDialogFrame";
import { FloatingComboboxField, FloatingTextField } from "./FloatingFields";

const DUPLICATE_DEBOUNCE_MS = 400;

type Step = "who" | "team";

const WHO_FIELDS: ReadonlyArray<keyof NewClientValues> = [
  "firstName",
  "middleName",
  "lastName",
  "email",
  "birthday",
  "phone",
  "preferredLanguage",
  "citizenship"
];

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLTextAreaElement ||
  (target instanceof HTMLElement && target.isContentEditable);

type Props = {
  firmName?: string | null;
  onCancel: () => void;
  /** Called with the new client's id once everything has saved. */
  onCreated: (uid: string) => void;
};

/**
 * New client as Linear's create modal: who the client is (optionally from a
 * passport scan, with a duplicate check), then who works with them.
 */
export const NewClientWizard: FC<Props> = ({
  firmName,
  onCancel,
  onCreated
}) => {
  const { lawyerPractice } = useUserRoleAndSubscription();
  const {
    userDetails: { id: currentUserId }
  } = useGetUserDetails();
  const { createForeignNational, isSaving } = useForeignNationals();
  const { ensure: ensureUserProfiles, get: getUserProfile } = useUserProfiles();
  const {
    teamMembers,
    isLoading: isLoadingTeam,
    error: teamError,
    refetch: refetchTeam
  } = usePracticeTeamMembers(lawyerPractice);

  const form = useForm<NewClientValues>({
    resolver: zodResolver(newClientSchema),
    defaultValues: emptyNewClient(currentUserId),
    mode: "onTouched"
  });
  const { setValue, getValues, setError, setFocus, trigger, control } = form;

  const frameRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<Step>("who");
  const [shown, setShown] = useState<Set<OptionalField>>(new Set());
  const [scan, setScan] = useState<{ data: PassportScan; file: File } | null>(
    null
  );
  const [matches, setMatches] = useState<DuplicateMatch[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (step === "who") setFocus("firstName");
    else frameRef.current?.focus({ preventScroll: true });
  }, [setFocus, step]);

  useEffect(() => {
    if (currentUserId && getValues("assignedUserIds").length === 0) {
      setValue("assignedUserIds", [currentUserId]);
    }
  }, [currentUserId, getValues, setValue]);

  useEffect(() => {
    const ids = teamMembers.map((m) => (m?.uid ?? "").trim()).filter(Boolean);
    if (ids.length > 0) ensureUserProfiles(ids);
  }, [ensureUserProfiles, teamMembers]);

  const [firstName, lastName, email, birthday, assignedUserIds] = useWatch({
    control,
    name: ["firstName", "lastName", "email", "birthday", "assignedUserIds"]
  });
  const query = useMemo(
    () => duplicateQuery({ firstName, lastName, email, birthday }),
    [birthday, email, firstName, lastName]
  );
  const queryKey = query ? JSON.stringify(query) : "";

  useEffect(() => {
    if (!queryKey) {
      setMatches([]);
      return undefined;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      ForeignNationalsAPI.findDuplicates(JSON.parse(queryKey))
        .then((found) => live && setMatches(found))
        .catch(() => live && setMatches([]));
    }, DUPLICATE_DEBOUNCE_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [queryKey]);

  const duplicates = useMemo(
    () => rankDuplicates(matches, dismissed),
    [dismissed, matches]
  );

  const reveal = (keys: OptionalField[]) =>
    setShown((prev) => new Set([...prev, ...keys]));

  const onScanned = (data: PassportScan, file: File) => {
    const filled = applyScan(data);
    Object.entries(filled.values).forEach(([key, value]) =>
      setValue(key as keyof NewClientValues, value as string, {
        shouldValidate: true,
        shouldDirty: true
      })
    );
    reveal(filled.reveal);
    setScan({ data, file });
  };

  const finish = async (uid: string, values: NewClientValues) => {
    const failures: string[] = [];
    await Promise.all(
      profileUpdates(values, scan?.data ?? null).map((payload) =>
        ForeignNationalsAPI.update(uid, payload).catch(() =>
          failures.push("citizenship and language")
        )
      )
    );
    const passport = passportFromScan(scan?.data ?? null);
    if (passport) {
      await ForeignNationalsAPI.createPassport(uid, passport).catch(() =>
        failures.push("passport")
      );
    }
    if (scan) {
      await ForeignNationalDocumentsAPI.upload(uid, scan.file).catch(() =>
        failures.push("passport scan")
      );
    }
    if (failures.length > 0) {
      toast.warning("Client created, but some details didn't save", {
        description: `Add the ${failures.join(", ")} from their profile.`
      });
    }
  };

  const onSubmit = async (values: NewClientValues) => {
    try {
      const created = await createForeignNational(toCreatePayload(values));
      setFinishing(true);
      await finish(created.uid, values);
      onCreated(created.uid);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (/EMAIL_EXISTS|already exists/i.test(message)) {
        setStep("who");
        setError("email", {
          message: "Another client or user already has this email."
        });
      }
    } finally {
      setFinishing(false);
    }
  };

  const busy = isSaving || finishing;
  const soloFirm = !isLoadingTeam && !teamError && teamMembers.length === 0;
  const needsAssignee =
    !isLoadingTeam && teamMembers.length > 0 && assignedUserIds.length === 0;
  const isLast = step === "team" || soloFirm;
  const hidden = OPTIONAL_FIELDS.filter((f) => !shown.has(f.key));

  const submit = () => void form.handleSubmit(onSubmit, () => setStep("who"))();

  const goForward = async () => {
    if (busy) return;
    if (step === "who" && !(await trigger([...WHO_FIELDS]))) return;
    if (isLast) {
      if (!needsAssignee) submit();
      return;
    }
    setStep("team");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // An open select or date picker consumes Enter to pick an option.
    if (event.defaultPrevented) return;
    if (event.key !== "Enter" || event.shiftKey) return;
    if (isTypingTarget(event.target)) return;
    if (event.target instanceof HTMLButtonElement) return;
    event.preventDefault();
    void goForward();
  };

  return (
    <FormProvider {...form}>
      <NewCaseDialogFrame
        ref={frameRef}
        title="New Client"
        firmName={firmName}
        progress={step === "who" ? 0 : 50}
        hint={
          step === "team" && needsAssignee
            ? "Pick at least one person"
            : step === "who"
            ? "Name is enough to start; add the rest later"
            : null
        }
        onBack={step === "team" ? () => setStep("who") : undefined}
        onCancel={onCancel}
        primaryLabel={isLast ? "Create Client" : "OK"}
        onPrimary={() => void goForward()}
        isPrimaryDisabled={step === "team" && needsAssignee}
        isPrimaryLoading={busy}
        onKeyDown={onKeyDown}
      >
        {step === "who" ? (
          <DialogQuestion stepNumber={1} title="Who is your client?">
            <div className="flex flex-col gap-4">
              <PassportScanDrop
                scanned={scan}
                onScanned={onScanned}
                onClear={() => setScan(null)}
              />
              <FormRow>
                <FloatingTextField<NewClientValues>
                  name="firstName"
                  label="First Name"
                  required
                />
                <FloatingTextField<NewClientValues>
                  name="lastName"
                  label="Last Name"
                  required
                />
              </FormRow>
              {shown.has("middleName") ? (
                <FloatingTextField<NewClientValues>
                  name="middleName"
                  label="Middle Name"
                />
              ) : null}
              <FloatingTextField<NewClientValues>
                name="email"
                label="Email Address"
                type="email"
                hint="Optional. Leave it blank for children and anyone who won't use the portal."
              />
              <DuplicateWarning
                matches={duplicates}
                onDismiss={(id) =>
                  setDismissed((prev) => new Set(prev).add(id))
                }
              />
              {shown.has("birthday") || shown.has("phone") ? (
                <FormRow>
                  {shown.has("birthday") ? (
                    <FloatingTextField<NewClientValues>
                      name="birthday"
                      label="Date of Birth"
                      type="date"
                      max={todayDateOnly()}
                    />
                  ) : null}
                  {shown.has("phone") ? (
                    <FloatingTextField<NewClientValues>
                      name="phone"
                      label="Phone Number"
                      type="tel"
                    />
                  ) : null}
                </FormRow>
              ) : null}
              {shown.has("preferredLanguage") || shown.has("citizenship") ? (
                <FormRow>
                  {shown.has("citizenship") ? (
                    <FloatingComboboxField<NewClientValues>
                      name="citizenship"
                      label="Country of Citizenship"
                      options={COUNTRY_OPTIONS}
                      searchPlaceholder="Search countries…"
                      emptyText="No country found."
                    />
                  ) : null}
                  {shown.has("preferredLanguage") ? (
                    <FloatingComboboxField<NewClientValues>
                      name="preferredLanguage"
                      label="Preferred Language"
                      groups={LANGUAGE_GROUPS}
                      searchPlaceholder="Search languages…"
                      emptyText="No language found."
                    />
                  ) : null}
                </FormRow>
              ) : null}
              {hidden.length > 0 ? (
                <OptionalFieldTags
                  fields={hidden}
                  onAdd={(key) => reveal([key])}
                />
              ) : null}
            </div>
          </DialogQuestion>
        ) : (
          <DialogQuestion stepNumber={2} title="Who's working with them?">
            <AssigneePicker
              members={teamMembers}
              value={assignedUserIds}
              onChange={(ids) =>
                setValue("assignedUserIds", ids, { shouldDirty: true })
              }
              isLoading={isLoadingTeam}
              error={teamError}
              onRetry={() => void refetchTeam()}
              getAvatarUrl={(uid) =>
                getUserProfile(uid)?.avatar_data_url ?? undefined
              }
              currentUserId={currentUserId}
            />
          </DialogQuestion>
        )}
      </NewCaseDialogFrame>
    </FormProvider>
  );
};
